import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import Page from "src/layouts/Page";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import HashtagForm, {
	HashtagFormValues
} from "../../components/HashtagForm/HashtagForm";
import useAdminHashtag from "../../hooks/useAdminHashtag";
import useAdminHashtags from "../../hooks/useAdminHashtags";
import useEditHashtag from "../../hooks/useEditHashtag";
import useDeleteHashtag from "../../hooks/useDeleteHashtag";
import { adaptEditHashtagPayload } from "../../adapters/adaptHashtagPayload";
import { formValuesToHashtag } from "../../adapters/formValuesToHashtag";

function hashtagToFormValues(hashtag: AdminHashtag): HashtagFormValues {
	return {
		name: hashtag.name,
		description: hashtag.description ?? "",
		color: hashtag.color,
		isPrivate: hashtag.isPrivate,
		isFavourite: hashtag.isFavourite,
		excludeFromMainFeed: hashtag.excludeFromMainFeed,
		image: null
	};
}

const EditHashtag = () => {
	const navigate = useNavigate();
	const { hashtagId } = useParams<{ hashtagId: string }>();
	const uuid = hashtagId ?? "";
	const UUID_RE =
		/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
	const isValidId = UUID_RE.test(uuid);

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [defaultValues, setDefaultValues] = useState<HashtagFormValues>({
		name: "",
		description: "",
		color: "#2563eb",
		isPrivate: false,
		isFavourite: false,
		excludeFromMainFeed: false,
		image: null
	});

	const { data: hashtag } = useAdminHashtag(isValidId ? uuid : "");
	const { data: allHashtags = [] } = useAdminHashtags();
	const existingNames = allHashtags.map((h) => h.name);
	const { mutate: editHashtag, isLoading: isEditing } = useEditHashtag();
	const { mutate: deleteHashtag, isLoading: isDeleting } = useDeleteHashtag();

	useEffect(() => {
		if (hashtag) {
			setDefaultValues(hashtagToFormValues(hashtag));
		}
	}, [hashtag]);

	if (!isValidId) {
		return <Navigate to="/social-feed/hashtags" replace />;
	}

	const handleSubmit = (data: HashtagFormValues, imageRemoved: boolean) => {
		const payload = adaptEditHashtagPayload(
			formValuesToHashtag(data),
			data.image,
			imageRemoved
		);

		editHashtag(
			{ uuid, payload },
			{
				onSuccess: () => navigate("/social-feed/hashtags")
			}
		);
	};

	const handleDelete = () => {
		deleteHashtag(uuid, {
			onSuccess: () => {
				setShowDeleteModal(false);
				navigate("/social-feed/hashtags");
			}
		});
	};

	return (
		<Page>
			<HashtagForm
				defaultValues={defaultValues}
				onSubmit={handleSubmit}
				isSubmitting={isEditing}
				isEdit
				onDelete={() => setShowDeleteModal(true)}
				existingImageUrl={hashtag?.image ?? null}
				existingNames={existingNames}
				currentName={hashtag?.name}
			/>

			<ConfirmationModal
				isOpen={showDeleteModal}
				title="Delete hashtag?"
				description="This action cannot be undone."
				onCancel={() => {
					if (!isDeleting) setShowDeleteModal(false);
				}}
				cancelBtnProps={{ disabled: isDeleting }}
				onSubmit={handleDelete}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default EditHashtag;
